/**
 * Phase 21.4 Batch 7 → Production fix — Track page contest badge.
 *
 * Renders 3 distinct states depending on entry status:
 *   1. Winner (status='winner'): 🥇/🥈/🥉 with contest badge + optional HoF chip.
 *   2. Entered (open/published contest): "Entered in Contest" with countdown.
 *   3. No entry: renders null.
 *
 * When the parent TrackPage receives the `rgm.contest.committed`
 * window event (dispatched by the Contest Control Center after a
 * commit), we refetch so the winner state appears immediately.
 */
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import ContestCountdown from "@/components/contests/ContestCountdown";
import { deriveContestDisplayStatus } from "@/lib/contestStatus";

const PLACEMENT_META = {
  1: { emoji: "🥇", label: "1st Place", tone: "border-amber-400/60 from-amber-900/40" },
  2: { emoji: "🥈", label: "2nd Place", tone: "border-gray-300/60 from-gray-700/40" },
  3: { emoji: "🥉", label: "3rd Place", tone: "border-orange-400/60 from-orange-900/40" },
};

const TrackContestBadge = ({ submissionId, artistUserId }) => {
  const [entry, setEntry] = useState(null);
  const [hof, setHof] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const fetchAll = useCallback(async () => {
    if (!submissionId) return;
    try {
      const r = await api.get(`/tracks/${submissionId}/contest-entry`);
      setEntry(r.data?.entry || null);
    } catch { setEntry(null); }
    if (artistUserId) {
      try {
        const r = await api.get(`/hall-of-champions/user/${artistUserId}`);
        const rows = r.data?.champions || [];
        // Match by entry_ref_id (winning track)
        const match = rows.find((h) => h.entry_ref_id === submissionId);
        setHof(match || null);
      } catch { setHof(null); }
    }
    setLoaded(true);
  }, [submissionId, artistUserId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    const handler = () => fetchAll();
    window.addEventListener("rgm.contest.committed", handler);
    return () => window.removeEventListener("rgm.contest.committed", handler);
  }, [fetchAll]);

  if (!loaded || !entry?.contest) return null;
  const c = entry.contest;
  const displayStatus = deriveContestDisplayStatus(c) || {};
  const showCountdown = c.submission_deadline
    && ["published", "open"].includes(c.status);

  // Winner state — shows the placement emoji + Contest Winner label + HoF chip.
  const isWinner = entry.status === "winner" && [1, 2, 3].includes(entry.position);
  if (isWinner) {
    const meta = PLACEMENT_META[entry.position];
    return (
      <Link
        to={`/contests/${c.slug || c.contest_id}`}
        className={`block rounded-xl border-2 ${meta.tone} bg-gradient-to-br to-black p-4 mb-4 hover:brightness-110 transition-all`}
        data-testid={`track-contest-winner-badge-${entry.entry_id}`}
      >
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-3xl" data-testid="track-contest-winner-emoji">{meta.emoji}</span>
          <div className="min-w-0 flex-1">
            <p className="text-[10px] uppercase tracking-widest text-amber-300 font-black">
              Contest Winner · {meta.label}
            </p>
            <p className="text-white font-black text-sm sm:text-base truncate" data-testid="track-contest-winner-title">
              {c.title}
            </p>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/25 border border-amber-500/50 text-amber-100 text-[10px] font-black"
                data-testid="track-contest-badge-chip"
              >
                🏆 Contest Winner
              </span>
              {hof && (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-500/25 border border-purple-500/50 text-purple-100 text-[10px] font-black"
                  data-testid="track-contest-hof-chip"
                >
                  🏛 Hall of Champions
                </span>
              )}
            </div>
          </div>
          <span className="text-[11px] text-purple-200 underline shrink-0">View contest →</span>
        </div>
      </Link>
    );
  }

  // Default state — "Entered in Contest" banner
  return (
    <Link
      to={`/contests/${c.slug || c.contest_id}`}
      className="block rounded-xl border border-amber-500/30 bg-gradient-to-br from-amber-900/25 via-purple-900/15 to-black p-3 sm:p-4 mb-4 hover:border-amber-400/60 hover:from-amber-900/40 transition-all"
      data-testid={`track-contest-badge-${entry.entry_id}`}
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-widest text-amber-300 font-black">
            🏆 Entered in Contest
          </p>
          <p
            className="text-white font-black text-sm sm:text-base truncate mt-0.5"
            data-testid="track-contest-badge-title"
          >
            {c.title}
          </p>
          <p className="text-[11px] text-gray-300 mt-1">
            Status: <span className="text-white font-black">{displayStatus.label || c.status}</span>
            {" · "}
            <span className="text-purple-200 underline">Join this contest →</span>
          </p>
        </div>
        {showCountdown && (
          <div
            className="text-right"
            data-testid="track-contest-badge-countdown"
          >
            <p className="text-[10px] uppercase tracking-widest text-gray-400 font-black">
              Submissions close in
            </p>
            <ContestCountdown targetIso={c.submission_deadline} label={null} />
          </div>
        )}
      </div>
    </Link>
  );
};

export default TrackContestBadge;
